'use client';

import { LoadingFallback } from '@order-up/common';
import React from 'react';
import { getAuthServiceUrl } from '../utils/env';
import { AuthenticatedSelectionGuard } from './AuthenticatedSelectionGuard';
import { AuthGuard } from './AuthGuard';
import type { SelectionWizardProps } from './SelectionWizard';
import { TokenHandler } from './TokenHandler';
import type { SelectionData, SelectionRequirements } from '../types/routes';

/**
 * Props for AuthenticatedRoute component
 */
export interface AuthenticatedRouteProps {
  children: React.ReactNode | ((data: SelectionData) => React.ReactNode);

  // Auth configuration
  authServiceUrl?: string;

  // Selection configuration
  requireSelection?: SelectionRequirements;
  selectionWizardProps?: Partial<SelectionWizardProps>;

  // UI customization
  loadingComponent?: React.ComponentType;
  errorComponent?: React.ComponentType<{ error: string }>;

  // Callbacks
  onSelectionComplete?: (data: SelectionData) => void;
  onAuthComplete?: (data: SelectionData) => void;
}

/**
 * Authenticated route component for applications requiring user authentication.
 * 
 * This component handles authenticated applications and provides:
 * - Token processing from auth service redirects
 * - User authentication validation
 * - Optional tenant/restaurant selection for multi-tenant apps
 * - Consistent auth flow across all authenticated applications
 * 
 * Security: Single-responsibility design focused only on authenticated
 * routes eliminates parameter confusion and reduces attack surface.
 */
export function AuthenticatedRoute({
  children,
  authServiceUrl,
  requireSelection,
  selectionWizardProps,
  loadingComponent: LoadingComponent = LoadingFallback,
  errorComponent: _ErrorComponent,
  onSelectionComplete,
  onAuthComplete
}: AuthenticatedRouteProps) {

  // Case 1: Simple authenticated app (no selection required)
  if (!requireSelection) {
    return (
      <TokenHandler authServiceUrl={authServiceUrl} onComplete={onAuthComplete} loadingComponent={LoadingComponent}>
        <AuthGuard
          authServiceUrl={authServiceUrl}
          loadingComponent={LoadingComponent}
          fallbackComponent={() => (
            <AuthFallback authServiceUrl={authServiceUrl} />
          )}
        >
          {typeof children === 'function' ? <>{children({})}</> : children}
        </AuthGuard>
      </TokenHandler>
    );
  }

  // Case 2: Authenticated app with tenant/restaurant selection
  return (
    <TokenHandler authServiceUrl={authServiceUrl} onComplete={onAuthComplete} loadingComponent={LoadingComponent}>
      <AuthGuard
        authServiceUrl={authServiceUrl}
        loadingComponent={LoadingComponent}
        fallbackComponent={() => (
          <AuthFallback authServiceUrl={authServiceUrl} />
        )}
      >
        <AuthenticatedSelectionGuard
          requireSelection={requireSelection}
          selectionWizardProps={selectionWizardProps}
          onSelectionComplete={onSelectionComplete}
          loadingComponent={LoadingComponent}
        >
          {children}
        </AuthenticatedSelectionGuard>
      </AuthGuard>
    </TokenHandler>
  );
}

/**
 * Fallback component for authentication required
 */
function AuthFallback({ authServiceUrl }: { authServiceUrl?: string }) {
  const handleSignIn = () => {
    const authUrl = authServiceUrl || getAuthServiceUrl() || '/auth';
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${authUrl}?returnUrl=${returnUrl}`;
  };

  return (
    <div className="min-h-svh bg-background flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        <h2 className="text-2xl font-bold mb-4">Authentication Required</h2>
        <p className="text-muted-foreground mb-6">
          Please sign in to access this page.
        </p>
        <button
          onClick={handleSignIn}
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2 px-4 rounded-md transition-colors"
        >
          Sign In
        </button>
      </div>
    </div>
  );
}